











































































/* General */
import Vue, { VueConstructor } from 'vue'
import { debounce } from 'lodash'
import Clickoutside from 'element-ui/src/utils/clickoutside'

/* Mixins */

/* Components */

/* Helpers */
import TextAssistant from '@/helpers/text-assistant'

/* Data types */
import { IPersonWithEmail } from '@/api/types/response.types'

/* API */
import MailboxesAPI from '@/api/modules/mailboxes.api'

export interface IDDEmailPicker {
  $refs: {
    toInputSelect: {
      query: string
      $refs: {
        input: HTMLElement
        tags: HTMLElement
      }
    }
    ccInputSelect: {
      query: string
      $refs: {
        input: HTMLElement
        tags: HTMLElement
      }
    }
  }
}

/* 
  This component is used for entering email addresses for conversations. It will load all existing
  customers from the passed-in mailbox ID so a user can select from the list (or type in their own)
*/
export default (Vue as VueConstructor<Vue & IDDEmailPicker>).extend({
  name: 'ToCcInputs',

  directives: { Clickoutside },

  props: {
    mailboxId: {
      type: Number,
      required: true,
    },
    /* For sole purpose of pre-stubbing "to" label with the name since the list of customers
       for the mailbox is not pulled unless the user types into the "to" or "cc" field.
       This will add the customer to the availableCustomers array on creation to allow el-select
       to display the name on the "to" label. If a user types into the "to" or "cc" fields,
       the availableCustomers array is re-hydrated from the DB. */
    initialToCustomer: {
      type: Object as () => IPersonWithEmail,
      required: false,
    },
    toEmail: {
      type: String,
      required: true,
    },
    ccdEmails: {
      type: Array as () => Array<string>,
      required: true,
    },
    focusTo: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isLoadingCustomers: false,
      shouldRefreshAvailableCustomers: true,

      availableCustomers: [] as IPersonWithEmail[],
      filteredAvailableCustomers: [] as IPersonWithEmail[],

      curToInputValue: '',
      curCcInputValue: '',

      debounceHandleToInputValue: null,
      debounceHandleCcInputValue: null,
    }
  },

  created() {
    this.debounceHandleToInputValue = debounce(this.handleToInputValue, 250)
    this.debounceHandleCcInputValue = debounce(this.handleCcInputValue, 250)
  },

  watch: {
    focusTo: {
      immediate: true,
      handler(val) {
        if (val) {
          // @ts-ignore
          this.$refs.toInputSelect.focus()
        }
      },
    },
    initialToCustomer: {
      immediate: true,
      handler(val) {
        if (val && val.id !== 0) {
          /* This is done so the to input's getToLabel() is set with the initial customer's name and email, otherwise
             you will only see the default in the selected TO: the value of the <el-option> which is just the email from the
             internalToEmailAsArray value */
          this.availableCustomers = [val]
          this.filteredAvailableCustomers = [val]
          /* Since cur customer has changed, need to restub "To" and refresh upon typing...*/
          this.shouldRefreshAvailableCustomers = true
        }
      },
    },
  },

  computed: {
    internalToEmail: {
      get(): String {
        return this.toEmail
      },
      set(val: string) {
        if (!TextAssistant.isValidEmail(val)) {
          val = ''
          // @ts-ignore
          this.$refs.toInputSelect.focus()
        } else {
          // @ts-ignore
          this.$refs.ccInputSelect.focus()
        }
        /* Can use sync or custom 'update-to-email' event to handle toEmail update */
        this.$emit('update:toEmail', val)
        this.$emit('update-to-email', val)
      },
    },
    internalToEmailAsArray: {
      get(): Array<String> {
        return this.toEmail.length > 0 ? [this.toEmail] : []
      },
      set(val: Array<string>) {
        this.internalToEmail = val.length > 0 ? val[0] : ''
      },
    },
    internalCcdEmails: {
      get(): Array<string> {
        return this.ccdEmails
      },
      set(val: Array<string>) {
        const cleansedVal = val.filter(v => TextAssistant.isValidEmail(v))
        /* Can use sync or custom 'update-ccd-emails' event to handle toEmail update */
        this.$emit('update:ccdEmails', cleansedVal)
        this.$emit('update-ccd-emails', cleansedVal)
      },
    },
    popperClass(): string {
      let result = 'options-dropdown '

      if (this.filteredAvailableCustomers.length === 0) {
        result += 'remove'
      }
      return result
    },
  },

  mounted() {
    this.addInputPlaceholders()

    this.$refs.toInputSelect.$refs.tags.addEventListener(
      'click',
      (event: MouseEvent) => {
        event.stopPropagation()
      },
    )
    this.$refs.ccInputSelect.$refs.input.addEventListener(
      'keyup',
      (event: KeyboardEvent) => {
        this.adjustCcDropdownPosition()
      },
    )
    this.$refs.ccInputSelect.$refs.tags.addEventListener(
      'click',
      (event: MouseEvent) => {
        event.stopPropagation()
      },
    )
  },

  methods: {
    getToLabel(customer: IPersonWithEmail): string {
      if (customer.name !== customer.email) {
        return customer.name + ' <' + customer.email + '>'
      }
      return customer.email
    },
    getOptionsHtmlLabel(customer: IPersonWithEmail): string {
      if (customer.name !== customer.email) {
        return (
          '<strong>' +
          customer.name +
          '</strong>&nbsp;&lt;' +
          customer.email +
          '&gt;'
        )
      }
      return customer.email
    },
    addInputPlaceholders() {
      const toInput: HTMLInputElement = this.$el.querySelector(
        '.to-input .el-select__input',
      )

      toInput.placeholder = 'Enter recipient address...'

      const ccInput: HTMLInputElement = this.$el.querySelector(
        '.cc-input .el-select__input',
      )

      ccInput.placeholder = 'Enter ccs...'
    },

    async filterAvailableCustomers(val: string) {
      if (this.$screen.isMobile(this.$mq)) {
        return
      }
      if (val.trim().length <= 1) {
        this.filteredAvailableCustomers = []
        return
      }
      // Skip load/prune if we are still in the customer API call...
      if (!this.isLoadingCustomers) {
        if (this.shouldRefreshAvailableCustomers) {
          try {
            this.isLoadingCustomers = true
            this.availableCustomers = await MailboxesAPI.get_Customers(
              this.mailboxId,
            )
            this.isLoadingCustomers = false
            this.shouldRefreshAvailableCustomers = false
          } catch (error) {
            this.$error.emit(error)
          }
        }

        const loweredVal = val.toLowerCase()

        this.filteredAvailableCustomers = this.availableCustomers
          .filter(
            i =>
              i.email.includes(loweredVal) ||
              i.name.toLowerCase().includes(loweredVal),
          )
          .slice(0, 200)
      }
    },

    attemptToEmailPush(val: string) {
      if (TextAssistant.isValidEmail(val)) {
        this.internalToEmail = val
      }
      this.clearInputFields()
    },

    attemptCcEmailsPush(val: string) {
      if (TextAssistant.isValidEmail(val)) {
        const newCcdEmails = [
          ...this.internalCcdEmails,
          val.toLowerCase().trim(),
        ]

        this.internalCcdEmails = Array.from(new Set(newCcdEmails))
      }
      this.clearInputFields()
    },

    clearInputFields() {
      this.curToInputValue = ''
      this.curCcInputValue = ''
      this.$refs.toInputSelect.query = ''
      this.$refs.ccInputSelect.query = ''
      this.filterAvailableCustomers('')
    },

    async handleToInputValue(val: string) {
      /* Store the current value separately. This will be used
      in case user focuses out of the select field so we can evaluate
      whether the last typed-in value needs to be processed. Otherwise,
      the 'this.$refs.toInputSelect.query' is null whenever we
      lose focus*/
      this.curToInputValue = val
      if (val.trim().length < 1) {
        return
      }

      // Skip load/prune if we are still in the customer API call...
      await this.filterAvailableCustomers(val)

      // Treat comma as a delimiter
      if (val.endsWith(',')) {
        const inputNoComma = val.slice(0, -1)
        // Checking if includes comma because if you just hold "," sometimes
        // handleCurInputValue will pass a value of some number of commas because of debouncing
        if (inputNoComma !== '' && !inputNoComma.includes(',')) {
          this.attemptToEmailPush(inputNoComma)
        }
      }
    },

    async handleCcInputValue(val: string) {
      /* Store the current value separately. This will be used
      in case user focuses out of the select field so we can evaluate
      whether the last typed-in value needs to be processed. Otherwise,
      the 'this.$refs.ccInputSelect.query' is null whenever we
      lose focus*/
      this.curCcInputValue = val

      this.adjustCcDropdownPosition()

      if (val.trim().length < 2) {
        return
      }

      // Skip load/prune if we are still in the customer API call...
      await this.filterAvailableCustomers(val)

      // Treat comma as a delimiter
      if (val.endsWith(',')) {
        const inputNoComma = val.slice(0, -1)
        // Checking if includes comma because if you just hold "," sometimes
        // handleCurInputValue will pass a value of some number of commas because of debouncing
        if (inputNoComma !== '' && !inputNoComma.includes(',')) {
          this.attemptCcEmailsPush(inputNoComma)
        }
      }
    },

    handleToDelimiter() {
      const inputValue = this.$refs.toInputSelect.query.trim()
      this.attemptToEmailPush(inputValue)
    },

    handleCcDelimiter() {
      const inputValue = this.$refs.ccInputSelect.query.trim()
      this.attemptCcEmailsPush(inputValue)
    },

    handleToVisibleChange(value: boolean) {
      /* If we are clicking out of a non-search only picker, 
         process any remaining value in the input. */
      if (!value) {
        this.attemptToEmailPush(this.curToInputValue)
      }
    },

    handleCcVisibleChange(value: boolean) {
      /* If we are clicking out of a non-search only picker, 
         process any remaining value in the input. */
      if (value) {
        /* Clear out list on focus so we don't automatically see the 
        pre-stubbed initialToCustomer in filteredAvailableCustomers in the suggest list (which
        is needed to get the getToLabel() value right on the initial load...so the user
        sees the name of the initial to customer). Otherwise,
        it shows it automatically on focus for some reason. */
        this.filteredAvailableCustomers = []
      } else {
        this.attemptCcEmailsPush(this.curCcInputValue)
      }
    },

    adjustCcDropdownPosition() {
      const input: HTMLInputElement = this.$el.querySelector(
        '.cc-input .el-select__input',
      )
      const dropdown: HTMLElement = this.$el.querySelector(
        '.cc-input .el-select-dropdown',
      )
      if (dropdown) {
        dropdown.style.marginLeft = input.offsetLeft + 'px'
      }
    },
  },
})
